/**
 * Themed modal primitives.
 *
 * - `confirmModal(opts)`: small yes/no dialog for destructive actions.
 * - `openDetailModal(opts)`: wider scrollable dialog for storefront detail
 *   views. The caller supplies a renderer that fills the modal body.
 *
 * Both trap focus, dismiss on ESC + click-outside, and respect prefers-
 * reduced-motion.
 */

interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export function confirmModal(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'mp-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'mp-modal-title');

    const dialog = document.createElement('div');
    dialog.className = 'mp-modal-dialog';

    const title = document.createElement('h3');
    title.id = 'mp-modal-title';
    title.className = 'mp-modal-title';
    title.textContent = opts.title;

    const body = document.createElement('p');
    body.className = 'mp-modal-body';
    if (opts.body) body.textContent = opts.body;

    const actions = document.createElement('div');
    actions.className = 'mp-modal-actions';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'ghost-btn';
    cancelBtn.textContent = opts.cancelLabel ?? 'Cancel';

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = opts.danger ? 'danger-btn primary-danger' : 'primary-btn';
    confirmBtn.textContent = opts.confirmLabel ?? 'Confirm';

    actions.append(cancelBtn, confirmBtn);
    dialog.append(title);
    if (opts.body) dialog.appendChild(body);
    dialog.appendChild(actions);
    overlay.appendChild(dialog);

    const prevFocus = document.activeElement as HTMLElement | null;

    const close = (result: boolean): void => {
      document.removeEventListener('keydown', onKey);
      overlay.classList.remove('in');
      setTimeout(() => {
        overlay.remove();
        prevFocus?.focus?.();
        resolve(result);
      }, 150);
    };

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(false);
        return;
      }
      if (e.key === 'Tab') {
        const focusables = [cancelBtn, confirmBtn];
        const idx = focusables.indexOf(document.activeElement as HTMLButtonElement);
        if (idx === -1) {
          e.preventDefault();
          focusables[0].focus();
          return;
        }
        const next = e.shiftKey ? (idx - 1 + focusables.length) % focusables.length : (idx + 1) % focusables.length;
        e.preventDefault();
        focusables[next].focus();
      }
    };

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close(false);
    });
    cancelBtn.addEventListener('click', () => close(false));
    confirmBtn.addEventListener('click', () => close(true));
    document.addEventListener('keydown', onKey);

    document.body.appendChild(overlay);
    requestAnimationFrame(() => {
      overlay.classList.add('in');
      (opts.danger ? cancelBtn : confirmBtn).focus();
    });
  });
}

/** Public handle for the detail modal — lets the renderer close it. */
export interface DetailModalHandle {
  close(): void;
  readonly host: HTMLDivElement;
}

interface DetailOptions {
  /** Called once with the scrollable content host. Mutate it freely. */
  renderBody: (host: HTMLDivElement) => void;
  /** Called when the modal is dismissed (any path). */
  onClose?: () => void;
}

/**
 * Open a wider scrollable modal. Designed for storefront detail views — the
 * renderer fills the body with hero / title / specs / admin row, and can call
 * `handle.close()` to dismiss programmatically.
 */
export function openDetailModal(opts: DetailOptions): DetailModalHandle {
  const overlay = document.createElement('div');
  overlay.className = 'mp-modal detail-modal';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');

  const dialog = document.createElement('div');
  dialog.className = 'mp-modal-dialog detail-modal-dialog';
  dialog.setAttribute('tabindex', '-1');

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'detail-modal-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '×';

  const host = document.createElement('div');
  host.className = 'detail-modal-body';

  dialog.append(closeBtn, host);
  overlay.appendChild(dialog);

  const prevFocus = document.activeElement as HTMLElement | null;
  let closed = false;

  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    overlay.classList.remove('in');
    setTimeout(() => {
      overlay.remove();
      prevFocus?.focus?.();
      opts.onClose?.();
    }, 160);
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') {
      // Trap focus within the dialog. Native tab order does the heavy lift —
      // we just bounce back to the dialog when focus leaves.
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusables.length === 0) return;
      const active = document.activeElement as HTMLElement | null;
      const idx = active ? focusables.indexOf(active) : -1;
      if (idx === -1) {
        e.preventDefault();
        focusables[0].focus();
        return;
      }
      if (e.shiftKey && idx === 0) {
        e.preventDefault();
        focusables[focusables.length - 1].focus();
      } else if (!e.shiftKey && idx === focusables.length - 1) {
        e.preventDefault();
        focusables[0].focus();
      }
    }
  };

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  closeBtn.addEventListener('click', close);
  document.addEventListener('keydown', onKey);

  document.body.appendChild(overlay);
  opts.renderBody(host);

  requestAnimationFrame(() => {
    overlay.classList.add('in');
    // Focus the dialog so ESC works immediately without a click.
    dialog.focus();
  });

  return { close, host };
}
